<div class="container px-0 max-w-100">
  <div class="row pt-4 mx-0 justify-content-center">
    <!-- Loading State -->
    <div class="col-12 text-center py-5" *ngIf="isLoading">
      <mat-spinner class="mx-auto"></mat-spinner>
      <p class="mt-3 text-muted">Loading payment details...</p>
    </div>

    <!-- Error State -->
    <div class="col-12 col-md-8 col-lg-6 p-2" *ngIf="hasError && !isLoading">
      <div class="card">
        <div class="card-body text-center py-5">
          <mat-icon class="text-danger" style="font-size: 64px; height: 64px; width: 64px;">error</mat-icon>
          <h4 class="mt-3">Unable to Load Payment Details</h4>
          <p class="text-muted">{{ errorMessage }}</p>
          <button mat-raised-button color="primary" (click)="goToDashboard()">
            Return to Dashboard
          </button>
        </div>
      </div>
    </div>

    <!-- Payment State (Processing or Completed) -->
    <div class="col-12 col-md-10 col-lg-8 p-2" *ngIf="!isLoading && !hasError && feeSummary">
      <div class="card confirmation-card">
        <!-- Processing Header -->
        <div class="card-header bg-warning text-dark text-center py-4" *ngIf="isPaymentProcessing()">
          <mat-spinner *ngIf="isRefreshing" diameter="48" class="mx-auto"></mat-spinner>
          <mat-icon *ngIf="!isRefreshing" style="font-size: 48px; height: 48px; width: 48px;">hourglass_top</mat-icon>
          <h3 class="mb-0 mt-2">Payment Processing</h3>
          <p class="mb-0 mt-1">Your payment is being processed. This may take a moment.</p>
          <button mat-raised-button class="mt-3" (click)="refreshPaymentStatus()" [disabled]="isRefreshing">
            <mat-icon class="align-middle me-1">refresh</mat-icon>
            {{ isRefreshing ? 'Checking...' : 'Check Status' }}
          </button>
        </div>

        <!-- Success Header -->
        <div class="card-header bg-success text-white text-center py-4" *ngIf="isPaymentCompleted()">
          <mat-icon style="font-size: 48px; height: 48px; width: 48px;">check_circle</mat-icon>
          <h3 class="mb-0 mt-2">Payment Successful!</h3>
          <p class="mb-0 mt-1">Your payment has been processed successfully</p>
        </div>

        <!-- Failed Header -->
        <div class="card-header bg-danger text-white text-center py-4" *ngIf="isPaymentFailed()">
          <mat-icon style="font-size: 48px; height: 48px; width: 48px;">error</mat-icon>
          <h3 class="mb-0 mt-2">Payment Failed</h3>
          <p class="mb-0 mt-1">There was an issue processing your payment</p>
        </div>

        <div class="card-body px-4 py-4">
          <!-- Confirmation Number (only when completed) -->
          <div class="text-center mb-4 pb-3 border-bottom" *ngIf="isPaymentCompleted() && feeSummary.transactionId">
            <small class="text-muted d-block">Confirmation Number</small>
            <h4 class="mb-0 text-primary">{{ feeSummary.transactionId }}</h4>
          </div>

          <!-- Processing Message -->
          <div class="alert alert-info mb-4" *ngIf="isPaymentProcessing()">
            <mat-icon class="align-middle me-2">info</mat-icon>
            Your payment is being finalized. Please click "Check Status" above to refresh.
            Do not navigate away from this page until processing is complete.
          </div>

          <!-- Failed Message -->
          <div class="alert alert-danger mb-4" *ngIf="isPaymentFailed()">
            <mat-icon class="align-middle me-2">warning</mat-icon>
            Your payment could not be completed. Please try again or contact support if the issue persists.
          </div>

          <!-- Payment Details -->
          <div class="row">
            <div class="col-12">
              <h5 class="mb-3">Payment Details</h5>
              <table class="table table-borderless">
                <tbody>
                  <tr>
                    <th class="text-muted" style="width: 40%;">Description:</th>
                    <td>{{ getDescription() }}</td>
                  </tr>
                  <tr>
                    <th class="text-muted">Date:</th>
                    <td>{{ feeSummary.transactionDate | date:'long'}}</td>
                  </tr>
                  <tr>
                    <th class="text-muted">Amount:</th>
                    <td>{{ feeSummary.feeTotal | currency }}</td>
                  </tr>
                  <tr *ngIf="feeSummary.creditUsed > 0">
                    <th class="text-muted">Credit Applied:</th>
                    <td class="text-success">-{{ feeSummary.creditUsed | currency }}</td>
                  </tr>
                  <tr>
                    <th class="text-muted">Amount Paid:</th>
                    <td class="fw-bold">{{ feeSummary.feeTotalPaid | currency }}</td>
                  </tr>
                </tbody>
              </table>
            </div>
          </div>

          <!-- Line Items -->
          <div class="row mt-3" *ngIf="feeSummary.items && feeSummary.items.length > 0">
            <div class="col-12">
              <h6 class="text-muted mb-2">Items</h6>
              <div class="bg-light p-3 rounded">
                <div *ngFor="let item of feeSummary.items" class="d-flex justify-content-between py-1">
                  <span>{{ item.name }}</span>
                  <span>{{ item.feeAmount | currency }}</span>
                </div>
              </div>
            </div>
          </div>

          <!-- Credit Information (only when completed) -->
          <div class="alert alert-success mt-4" *ngIf="isPaymentCompleted()">
            <mat-icon class="align-middle me-2">account_balance_wallet</mat-icon>
            <strong>Credit Added:</strong> {{ feeSummary.feeTotal | currency }} has been credited to your TEACH account.
            This credit can be applied to future certification application fees.
            Fees will remain on file for one year from the date the fee was paid. No refunds.
          </div>

          <!-- Actions -->
          <div class="row mt-4 pt-3 border-top">
            <div class="col-12 col-md-6 mb-2">
              <button mat-stroked-button color="primary" class="w-100" (click)="makeAnotherPayment()">
                <mat-icon class="align-middle me-1">add</mat-icon>
                Make Another Payment
              </button>
            </div>
            <div class="col-12 col-md-6 mb-2">
              <button mat-raised-button color="primary" class="w-100" (click)="goToDashboard()">
                <mat-icon class="align-middle me-1">home</mat-icon>
                Return to Dashboard
              </button>
            </div>
          </div>
        </div>

        <!-- Footer -->
        <div class="card-footer bg-light text-center py-3">
          <small class="text-muted">
            A copy of this confirmation has been saved to your account.
            <br />
            For questions about this payment, please contact the Alabama State Department of Education.
          </small>
        </div>
      </div>
    </div>
  </div>
</div>
